import React, { useEffect, useRef, useState } from "react";
import { FaceMesh } from "@mediapipe/face_mesh";
import {
  Box,
  Button,
  LinearProgress,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { motion, useAnimationFrame } from "framer-motion";
import { Close } from "@mui/icons-material";
import rotateDeviceAnimation from "../../assets/images/Animation - 1744884056415.json";
import faceScanAnimation from "../../assets/images/face_scan.json";
import loaderAnimation from "../../assets/images/green_loader.json";
import Lottie from "lottie-react";



const KNOWN_IPD_MM = 63;
const PROFILES = ["Front Profile", "Left Profile", "Right Profile"];

const ProgressiveCurveAnimation = ({ direction = "right" }) => {
  const NUM_CURVES = 10;
  const centerX = 480;
  const centerY = 245;
  const maskHeight = 380;
  const maskWidth = 400;

  const controlOffset = direction === "left" ? -maskWidth / 2 : maskWidth / 2;
  const lines = [];

  for (let i = 0; i < NUM_CURVES; i++) {
    const spread = (i / (NUM_CURVES - 1) - 0.5) * maskWidth * 0.85;

    const d = `
      M ${centerX},${centerY - maskHeight / 2}
      Q ${centerX + controlOffset + spread},${centerY}
      ${centerX},${centerY + maskHeight / 2}
    `;

    lines.push(
      <motion.path
        key={i}
        d={d}
        stroke="white"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 0] }}
        transition={{
          duration: 1.8,
          delay: i * 0.12,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    );
  }

  // Arrow inside mask
  const arrowOffset = direction === "right" ? maskWidth / 14 : -maskWidth / 14;
  const arrowShift = direction === "right" ? 10 : -10;

  const arrowBaseX = centerX + arrowOffset;
  const arrowTipX = arrowBaseX + (direction === "right" ? 80 : -80);

  return (
    <svg
      viewBox="0 0 960 540"
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: 10,
        pointerEvents: "none",
      }}
    >
      {lines}

      <motion.line
        x1={arrowBaseX}
        y1={centerY}
        x2={arrowTipX}
        y2={centerY}
        stroke="white"
        strokeWidth="5"
        markerEnd="url(#arrowhead)"
        animate={{
          x1: [0, arrowShift, 0].map((v) => arrowBaseX + v),
          x2: [0, arrowShift, 0].map((v) => arrowTipX + v),
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <defs>
        <marker
          id="arrowhead"
          markerWidth="10"
          markerHeight="7"
          refX="0"
          refY="3.5"
          orient="auto"
        >
          <polygon points="0 0, 10 3.5, 0 7" fill="white" />
        </marker>
      </defs>
    </svg>
  );
};
const HeadMeasurementModal = ({ onClose, onMeasurementComplete }) => {
  const videoRef = useRef(null);
  const [showRotatePrompt, setShowRotatePrompt] = useState(false);

  const canvasRef = useRef(null);
  const [faceMesh, setFaceMesh] = useState(null);
  const [profileStep, setProfileStep] = useState(0);
  const stableFrames = useRef(0);
  const cameraRef = useRef(null);

  const profileStepRef = useRef(0);

  const [started, setStarted] = useState(false);
  const [direction, setDirection] = useState(null);

  const [capturedLandmarks, setCapturedLandmarks] = useState([]);
  const [message, setMessage] = useState("");
  const [results, setResults] = useState(null);
  const measurementInProgress = started && !results;
  const showResults = Boolean(results);

  useEffect(() => {
    const mesh = new FaceMesh({
      locateFile: (file) =>
        `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
    });
    mesh.setOptions({
      maxNumFaces: 1,
      refineLandmarks: true,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });
    mesh.onResults(handleResults);
    setFaceMesh(mesh);
  }, []);

  // useEffect(() => {
  //   const loadCamera = async () => {
  //     await import(
  //       "https://cdn.jsdelivr.net/npm/@mediapipe/camera_utils/camera_utils.js"
  //     );
  //     const camera = new window.Camera(videoRef.current, {
  //       onFrame: async () => {
  //         await faceMesh.send({ image: videoRef.current });
  //       },
  //       width: 640,
  //       height: 480,
  //     });
  //     cameraRef.current = camera;
  //     camera.start();
  //   };
  //   if (faceMesh && videoRef.current) loadCamera();
  // }, [faceMesh]);

  useEffect(() => {
    const loadCamera = async () => {
      try {
        // Fetch the script using axios
        const scriptUrl = "https://cdn.jsdelivr.net/npm/@mediapipe/camera_utils/camera_utils.js";
  
        const script = document.createElement("script");
        script.src = scriptUrl;
        script.async = true;
  
        script.onload = () => {
          const camera = new window.Camera(videoRef.current, {
            onFrame: async () => {
              await faceMesh.send({ image: videoRef.current });
            },
            width: 640,
            height: 480,
          });
          cameraRef.current = camera;
          camera.start();
        };
  
        document.body.appendChild(script);
      } catch (error) {
        console.error("Failed to load MediaPipe camera_utils:", error);
      }
    };
  
    if (faceMesh && videoRef.current) loadCamera();
  }, [faceMesh]);

  useEffect(() => {
    const checkOrientation = () => {
      const isMobile = window.innerWidth <= 768;
      const isPortrait = window.matchMedia("(orientation: portrait)").matches;
      setShowRotatePrompt(isMobile && isPortrait);
    };

    checkOrientation();
    window.addEventListener("resize", checkOrientation);
    window.addEventListener("orientationchange", checkOrientation);

    return () => {
      window.removeEventListener("resize", checkOrientation);
      window.removeEventListener("orientationchange", checkOrientation);
    };
  }, []);



  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    if (cameraRef.current) {
      cameraRef.current.stop();
      cameraRef.current = null;
    }
  };

  const speak = (text) => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = "en-US";
    utter.rate = 1.0;
    synth.cancel();
    synth.speak(utter);
  };
  const lastSpokenMessage = useRef("");

  const hasAlreadyCaptured = capturedLandmarks.length > profileStepRef.current;
  const smoothingBuffer = useRef([]);
  const startedRef = useRef(false);

  const perProfileBuffers = useRef([[], [], []]);

  const handleResults = (results) => {
    if (!startedRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = "rgba(0, 0, 0, 0.3)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.scale(-1, 1);
    ctx.drawImage(results.image, -canvas.width, 0, canvas.width, canvas.height);
    ctx.restore();

    if (
      profileStepRef.current >= 3 ||
      !results.multiFaceLandmarks ||
      results.multiFaceLandmarks.length === 0
    )
      return;

    const landmarks = results.multiFaceLandmarks[0];
    const currentProfile = PROFILES[profileStepRef.current];
    const [aligned, msg] = isProfileAligned(
      landmarks,
      currentProfile,
      canvas.width,
      canvas.height
    );
    setMessage(msg);
    if (lastSpokenMessage.current !== msg) {
      speak(msg);
      lastSpokenMessage.current = msg;
    }
    setMessage(msg);

    if (msg.includes("Turn Left")) setDirection("left");
    else if (msg.includes("Turn Right")) setDirection("right");

    if (aligned) {
      stableFrames.current += 1;
      perProfileBuffers.current[profileStepRef.current].push(
        JSON.parse(JSON.stringify(landmarks))
      );

      if (perProfileBuffers.current[profileStepRef.current].length > 60) {
        perProfileBuffers.current[profileStepRef.current].shift();
      }

      const hasAlreadyCaptured =
        capturedLandmarks.length > profileStepRef.current;
      const requiredFrames = profileStepRef.current === 0 ? 60 : 30;

      if (stableFrames.current >= requiredFrames && !hasAlreadyCaptured) {
        const buffer = perProfileBuffers.current[profileStepRef.current];
        const median = medianLandmarkFrame(buffer);

        if (!median) {
          console.warn("🚫 Median landmark unstable. Retrying.");
          return;
        }

        setCapturedLandmarks((prev) => [...prev, median]);
        perProfileBuffers.current[profileStepRef.current] = [];
        stableFrames.current = 0;
        profileStepRef.current += 1;
        setProfileStep(profileStepRef.current);
      }
    } else {
      stableFrames.current = 0;
      perProfileBuffers.current[profileStepRef.current] = [];
    }
  };

  const medianLandmarkFrame = (frames, profile) => {
    const allowedVariance = profile === 0 ? 0.00001 : 0.00005;
    const checkVariance = (index) => {
      const values = frames.map((f) => [f[index].x, f[index].y]);
      const mean = values
        .reduce((acc, val) => [acc[0] + val[0], acc[1] + val[1]], [0, 0])
        .map((v) => v / values.length);
      const variance =
        values.reduce(
          (acc, val) =>
            acc +
            (Math.pow(val[0] - mean[0], 2) + Math.pow(val[1] - mean[1], 2)),
          0
        ) / values.length;
      return variance;
    };

    const criticalLandmarks =
      profile === 0 ? [234, 454, 33, 263] : [127, 356, 33, 263];

    for (let idx of criticalLandmarks) {
      const varCheck = checkVariance(idx);
      if (varCheck > allowedVariance) {
        console.warn(`${idx} too unstable. Variance: ${varCheck}`);
        return null;
      }
    }

    return Array.from({ length: 478 }, (_, i) => {
      const xList = frames.map((f) => f[i].x).sort((a, b) => a - b);
      const yList = frames.map((f) => f[i].y).sort((a, b) => a - b);
      const zList = frames.map((f) => f[i].z).sort((a, b) => a - b);
      const mid = Math.floor(frames.length / 2);
      return { x: xList[mid], y: yList[mid], z: zList[mid] };
    });
  };

  const isProfileAligned = (landmarks, profile, imageWidth, imageHeight) => {
    const x = (id) => landmarks[id].x * imageWidth;
    const y = (id) => landmarks[id].y * imageHeight;

    const noseX = x(1);
    const noseY = y(1);
    const leftEarX = x(234);
    const rightEarX = x(454);
    const chinY = y(152);
    const foreheadY = y(10);

    const leftDistance = Math.abs(noseX - leftEarX);
    const rightDistance = Math.abs(noseX - rightEarX);

    // Check if the face is inside the mask
    const maskCenterX = imageWidth / 2;
    const maskCenterY = imageHeight * 0.45;
    const maskRadiusX = 200;
    const maskRadiusY = 210;

    const isInsideMask =
      Math.pow((noseX - maskCenterX) / maskRadiusX, 2) +
        Math.pow((noseY - maskCenterY) / maskRadiusY, 2) <=
      1;

    if (!isInsideMask) return [false, "Align your face inside the mask"];

    if (profile === "Front Profile") {
      const leftEyeX = x(33);
      const rightEyeX = x(263);
      const faceCenterX = (leftEyeX + rightEyeX) / 2;

      // Check if the nose is centered
      if (Math.abs(noseX - faceCenterX) > 20) return [false, "Look Straight"];
      if (Math.abs(rightEyeX - leftEyeX) < imageWidth * 0.1)
        return [false, "Move Closer"];
      if (Math.abs(rightEarX - leftEarX) < imageWidth * 0.2)
        return [false, "Move Closer"];
      // Check vertical alignment
      const verticalLength = Math.abs(chinY - foreheadY);
      if (verticalLength > imageHeight * 0.45)
        return [false, "Too Close – Move Back"];

      if (verticalLength < imageHeight * 0.1) return [false, "Come Closer"];
      // Check if the face is tilted
      const leftEyeY = y(33);
      const rightEyeY = y(263);

      return [true, "Front Profile Aligned, Don't Move!"];
    }

    if (profile === "Right Profile") {
      if (noseX > leftEarX - 15) return [false, "Turn Right"];

      if (Math.abs(leftDistance - rightDistance) < imageWidth * 0.05)
        return [false, "Turn Right"];
      return [true, "Right Profile Aligned, Don't Move!"];
    }

    if (profile === "Left Profile") {
      if (noseX < rightEarX + 15) return [false, "Turn Left"];
      if (Math.abs(rightDistance - leftDistance) < imageWidth * 0.05)
        return [false, "Turn Left"];
      return [true, "Left Profile Aligned, Don't Move!"];
    }

    return [false, "Unknown Profile"];
  };
  const calculateMeasurements = (landmarksList, width, height) => {
    const [front, left, right] = landmarksList;

    const getPoint = (lm, id) => [lm[id].x * width, lm[id].y * height];
    const distance = (a, b) =>
      Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2);

    const leftEye = getPoint(front, 33);
    const rightEye = getPoint(front, 263);
    const ipdPx = distance(leftEye, rightEye);
    if (ipdPx === 0) return null;

    const scale = KNOWN_IPD_MM / ipdPx;

    const ipdCm = (ipdPx * scale) / 10;
    if (ipdCm < 5.5 || ipdCm > 7.5) return null;

    const leftSide = getPoint(front, 234);
    const rightSide = getPoint(front, 454);

    const temporalPixelWidth = distance(leftSide, rightSide);
    const temporalWidth = (temporalPixelWidth * scale) / 10 + 4.4;

    const leftEar = getPoint(front, 454);
    const rightEar = getPoint(front, 234);
    const chin = getPoint(front, 152);
    const forehead = [
      (getPoint(front, 10)[0] +
        getPoint(front, 151)[0] +
        getPoint(front, 9)[0]) /
        3,
      (getPoint(front, 10)[1] +
        getPoint(front, 151)[1] +
        getPoint(front, 9)[1]) /
        3,
    ];

    const headWidth = distance(leftEar, rightEar);
    const headHeight = distance(chin, forehead);
    const aspectRatio = headHeight / headWidth;

    let foreheadToBackhead = 0;
    if (aspectRatio > 1.3) {
      foreheadToBackhead =
        ((headWidth + headHeight * 1.1) * Math.PI * scale) / 10 - 6;
    } else if (aspectRatio < 0.9) {
      foreheadToBackhead =
        ((headWidth * 1.1 + headHeight) * Math.PI * scale) / 10 - 5;
    } else {
      foreheadToBackhead =
        ((headWidth + headHeight * 1.05) * Math.PI * scale) / 10 - 6;
    }

    const leftTemporal = getPoint(front, 127);
    const rightTemporal = getPoint(front, 356);

    const temporalMid = [
      (leftTemporal[0] + rightTemporal[0]) / 2,
      (leftTemporal[1] + rightTemporal[1]) / 2,
    ];

    const temporallWidth = distance(leftTemporal, rightTemporal);
    const foreheadToMid = distance(forehead, temporalMid);

    const estimatedTopHead = [forehead[0], forehead[1] - foreheadToMid * 2.2];

    const leftToTop = distance(leftTemporal, estimatedTopHead);
    const rightToTop = distance(rightTemporal, estimatedTopHead);

    let upperCirc = ((leftToTop + rightToTop + temporallWidth) * scale) / 10;
    upperCirc += 7;

    return {
      "Temporal Arc (cm)": upperCirc.toFixed(2),
      "Temporal Width (cm)": temporalWidth.toFixed(2),
      "Head Circumference (cm)": foreheadToBackhead.toFixed(2),
    };
  };

  useEffect(() => {
    if (capturedLandmarks.length === 3) {
      console.log("All 3 profiles captured:", capturedLandmarks);
      const canvas = canvasRef.current;
      try {
        const result = calculateMeasurements(capturedLandmarks, 800, 600);

        console.log("📏 Result:", result);
        if (result) {
          setTimeout(() => {
            setResults(result);
            setMessage("Measurement Completed !!");
            speak("Measurement Completed !!");
          }, 2000); // 2 second loader before results
        }

        if (onMeasurementComplete) {
          onMeasurementComplete(result);
        }
      } catch (err) {
        console.error(" Error during measurement:", err);
        setMessage("Measurement failed. Please retry.");
      }
    }
  }, [capturedLandmarks]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: "960px",
        height: "auto",
        aspectRatio: "16/9",
        overflow: "hidden",
        backgroundColor: "black",
        margin: "0 auto",
        minHeight: { xs: "80vh", sm: "60vh", md: "auto" },
      }}
    >
      <Button
        onClick={() => {
          stopCamera();
          onClose();
        }}
        sx={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 10,
          backgroundColor: "rgba(0,0,0,0.6)",
          color: "white",
          fontWeight: "bold",
          minWidth: 0,
          width: 36,
          height: 36,
          borderRadius: "50%",
          "&:hover": {
            backgroundColor: "rgba(255,255,255,0.1)",
          },
        }}
      >
        <Close />
      </Button>

      {/* Mirrored camera */}
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "auto",
          aspectRatio: "16/9",
          objectFit: "cover",
          transform: "scaleX(-1)",
          filter: !started || showResults ? "blur(8px)" : "none",
          zIndex: 1,
        }}
      />

      {/* Canvas overlay for face mesh */}
      <canvas
        ref={canvasRef}
        width="800"
        height="600"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          aspectRatio: "16/9",
          width: "100%",
          height: "100%",
          zIndex: 2,
          pointerEvents: "none",
        }}
      />

      {!started && (
        <Box
          sx={{
            position: "absolute",
            zIndex: 3,
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.65)",
            backdropFilter: "blur(8px)",
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            style={{ width: "100%", height: "100%", position: "relative" }}
          >
            {/* 1) SIDE BY SIDE CONTENT */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "row", md: "row" },
                width: "100%",
                height: "100%",
              }}
            >
              {/* — Left: Bigger Lottie */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 2,
                }}
              >
                <Lottie
                  animationData={faceScanAnimation}
                  loop
                  style={{ width: "60%", maxWidth: 300, height: "auto" }}
                />
              </Box>

              {/* — Right: Instructions */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  px: 4,
                  textAlign: { xs: "left", md: "left" },
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ color: "#fff", fontWeight: 600, mb: 2 }}
                >
                  Get Ready for Your Measurement !!
                </Typography>
                <Typography variant="body1" sx={{ color: "#ccc", mb: 1 }}>
                  Takes less than a minute. Please follow the steps:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    listStyle: "disc inside",
                    color: "#eee",
                    fontSize: "1rem",
                    lineHeight: 1.6,
                    m: 0,
                  }}
                >
                  <li>Remove your glasses</li>
                  <li>Ensure ears are clearly visible</li>
                  <li>Take off face masks or coverings</li>
                </Box>
              </Box>
            </Box>

            {/* 2) BUTTON AT BOTTOM CENTER */}
            <Box
              sx={{
                position: "absolute",
                bottom: 24,
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Button
                variant="contained"
                onClick={() => {
                  setStarted(true);
                  startedRef.current = true;
                  stableFrames.current = 0;
                  profileStepRef.current = 0;
                  setProfileStep(0);
                  setCapturedLandmarks([]);
                }}
                sx={{
                  backgroundColor: "#00e676",
                  color: "#000",
                  fontWeight: 600,
                  fontSize: "1.1rem",
                  px: 6,
                  py: 1.5,
                  borderRadius: "12px",
                  boxShadow: "0 6px 24px rgba(0, 230, 118, 0.4)",
                  "&:hover": { backgroundColor: "#00c853" },
                }}
              >
                START MEASUREMENT
              </Button>
            </Box>
          </motion.div>
        </Box>
      )}

      {measurementInProgress && (
        <>
          <Box
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              zIndex: 4,
              pointerEvents: "none",
            }}
          >
            <svg width="100%" height="100%">
              <ellipse
                cx="50%"
                cy="45%"
                rx="20%"
                ry="35%"
                fill="none"
                stroke={message.includes("Aligned") ? "#00e676" : "#ff5252"} // green or red
                strokeWidth="5"
                strokeDasharray="12,6"
                style={{
                  filter: message.includes("Aligned")
                    ? "drop-shadow(0 0 6px #00e676aa)"
                    : "drop-shadow(0 0 6px #ff5252aa)",
                  transition: "all 0.4s ease-in-out",
                }}
              />
            </svg>
          </Box>

          <Box
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              zIndex: 3,
              pointerEvents: "none",
            }}
          >
            <svg width="100%" height="100%">
              <defs>
                <mask id="ovalMask">
                  <rect width="100%" height="100%" fill="white" />
                  <ellipse cx="50%" cy="45%" rx="20%" ry="35%" fill="black" />
                </mask>
              </defs>
              <foreignObject width="100%" height="100%" mask="url(#ovalMask)">
                <Box
                  component="div"
                  sx={{
                    width: "100%",
                    height: "100%",
                    backdropFilter: "blur(10px)",
                    backgroundColor: "rgba(0,0,0,0.6)",
                  }}
                />
              </foreignObject>
              {PROFILES === "Left Profile" && (
                <path
                  d="M 100,50 C 90,80 70,120 50,150"
                  stroke="white"
                  strokeWidth="2"
                  fill="none"
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    from="0"
                    to="100"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </path>
              )}

              {PROFILES === "Right Profile" && (
                <path
                  d="M 100,50 C 110,80 130,120 150,150"
                  stroke="white"
                  strokeWidth="2"
                  fill="none"
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    from="0"
                    to="100"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </path>
              )}
              {message.includes("Turn Left") && (
                <ProgressiveCurveAnimation direction="left" />
              )}
              {message.includes("Turn Right") && (
                <ProgressiveCurveAnimation direction="right" />
              )}
            </svg>
          </Box>
        </>
      )}

      {showResults && (
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            zIndex: 4,
            backgroundColor: "rgba(0, 0, 0, 0.65)",
            backdropFilter: "blur(12px)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            style={{
              background: "linear-gradient(145deg, #1b1b1b, #242424)",
              borderRadius: 16,
              padding: 32,
              boxShadow: "0 12px 40px rgba(0,255,128,0.35)",
              width: "90%",
              maxWidth: 480,
              textAlign: "center",
            }}
          >
            <Typography
              variant="h4"
              sx={{ color: "#00e676", fontWeight: 700, mb: 2 }}
            >
              Results
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {Object.entries(results).map(([key, value]) => (
                <Box
                  key={key}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    backgroundColor: "rgba(255,255,255,0.05)",
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    color: "#fff",
                    fontSize: "1rem",
                    fontWeight: 500,
                  }}
                >
                  <span>{key}</span>
                  <span style={{ fontWeight: 700 }}>{value}</span>
                </Box>
              ))}
              <Typography
                variant="body2"
                color="#D3D3D3"
                sx={{ mt: 1, fontStyle: "italic" }}
              >
                This data will be auto-filled in the form.
              </Typography>
            </Box>
          </motion.div>
        </Box>
      )}
      {showResults && (
        <Button
          variant="contained"
          onClick={() => {
            setStarted(true);
            startedRef.current = true;
            stableFrames.current = 0;
            profileStepRef.current = 0;
            setProfileStep(0);
            setCapturedLandmarks([]);
            setResults(null);
            setMessage("");
          }}
          sx={{
            position: "absolute",
            bottom: 30,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 5,

            backgroundColor: "#00c853",
            color: "#000",
            fontWeight: 600,
            fontSize: "1.1rem",

            borderRadius: "12px",

            "&:hover": { backgroundColor: "#00c853" },

            px: 4,
            py: 1.5,
          }}
        >
          Restart Measurement
        </Button>
      )}
      {started && message && (
        <Typography
          sx={{
            position: "absolute",
            top: "20px",
            left: 0,
            right: 0,
            textAlign: "center",
            color: "white",
            fontWeight: "bold",
            fontSize: "1.2rem",
            zIndex: 5,
          }}
        >
          {message}
        </Typography>
      )}

      {started && !showResults && profileStep < 3 && (
        <Box
          sx={{
            position: "absolute",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            width: "80%",
            zIndex: 5,
          }}
        >
          {/* Green-themed Progress Bar */}
          <Box
            sx={{
              position: "relative",
              height: 26,
              borderRadius: 16,
              background: "rgba(255, 255, 255, 0.05)",
              overflow: "hidden",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(0, 230, 118, 0.2)",
              boxShadow: "0 2px 6px rgba(0,255,128,0.4)",
            }}
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(profileStep / 3) * 100}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              style={{
                height: "100%",
                backgroundColor: "#00e676", // Bright green
                boxShadow: "0 0 10px #00e676aa",
              }}
            />

            {/* % Label */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                fontWeight: 600,
                fontSize: 14,
                textShadow: "0 0 4px rgba(0,0,0,0.6)",
              }}
            >
              {Math.round((profileStep / 3) * 100)}%
            </Box>
          </Box>

          {/* Step Labels */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: 1,
              px: 0.5,
              color: "#ccc",
              fontSize: 13,
              fontWeight: 500,
              textTransform: "uppercase",
            }}
          >
            <span>Front</span>
            <span>Left</span>
            <span>Right</span>
          </Box>
        </Box>
      )}

      {started && profileStep === 3 && !showResults && (
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            zIndex: 10,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Lottie
            animationData={loaderAnimation}
            loop
            style={{ width: 120, height: 120 }}
          />
          <Typography sx={{ color: "#00e676", fontWeight: 600, mt: 2 }}>
            Finalizing measurement...
          </Typography>
        </Box>
      )}

      {showRotatePrompt && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.1)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            px: 2,
          }}
        >
          <Lottie
            animationData={rotateDeviceAnimation}
            loop
            style={{ width: 180, height: 180 }}
          />

          <Typography
            sx={{
              mt: 2,
              fontSize: "1.1rem",
              fontWeight: 500,
              textAlign: "center",
              color: "#fff",
            }}
          >
            Please Rotate your Phone
            <br />
            to landscape mode
          </Typography>
        </Box>
      )}
    </Box>
  );
};
export default HeadMeasurementModal;
